// History series as the charts draw them (WEB-F-DEV-04). vision-api answers [t, value] for raw values and for the
// aggregates min, max, sum and last, and [t, min, avg, max] for avg; a null value is a gap, which the line breaks at.
import type { HistorySeries } from "../generated/widget-fixture";

export interface ChartSeries {
  /** Milliseconds since the epoch, one per point. */
  times: number[];
  /** The value drawn: the raw value, the aggregate, or avg; null where there is a gap. */
  values: (number | null)[];
  /** For avg: the bucket's min and max, drawn as a band around the line. */
  band?: { min: (number | null)[]; max: (number | null)[] };
  /** Spans of late (backfilled) or estimated-time samples, [from, to) in milliseconds (vision-api 0.14.0). */
  quality: { from: number; to: number; flag: "late" | "estimated" }[];
}

type Quality = { from: string; to: string; flag: string };

/** vision-api 0.14.0 HistorySeries.quality; the widget-fixture model does not carry it yet. */
function spans(h: unknown): ChartSeries["quality"] {
  const q = (h as { quality?: unknown } | undefined)?.quality;
  if (!Array.isArray(q)) return [];
  return (q as Quality[]).flatMap((s) => {
    const from = Date.parse(s?.from);
    const to = Date.parse(s?.to);
    return (s?.flag === "late" || s?.flag === "estimated") && from < to ? [{ from, to, flag: s.flag }] : [];
  });
}

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);

export function toChartSeries(h: HistorySeries | undefined): ChartSeries {
  const times: number[] = [];
  const values: (number | null)[] = [];
  const avg = h?.aggregate === "avg";
  const min: (number | null)[] = [];
  const max: (number | null)[] = [];
  for (const p of h?.points ?? []) {
    const t = typeof p[0] === "string" ? Date.parse(p[0]) : NaN;
    if (Number.isNaN(t)) continue;
    times.push(t);
    if (avg && p.length >= 4) {
      min.push(num(p[1]));
      values.push(num(p[2]));
      max.push(num(p[3]));
    } else values.push(num(p[1]));
  }
  const quality = spans(h);
  return avg ? { times, values, band: { min, max }, quality } : { times, values, quality };
}

/** Lowest and highest drawn value, and the last one that is not a gap: the chart's text alternative. */
export function summarise(s: ChartSeries): { min: number; max: number; last: number } | undefined {
  const present = s.values.filter((v): v is number => v !== null);
  const last = present.at(-1);
  if (last === undefined) return undefined;
  return { min: Math.min(...present), max: Math.max(...present), last };
}

const UNIT_MS = { m: 60_000, h: 3_600_000, d: 86_400_000 } as const;

/** "24h", "7d", "15m" → milliseconds; undefined for anything else. */
export function durationMs(range: unknown): number | undefined {
  const m = typeof range === "string" ? /^([0-9]+)(m|h|d)$/.exec(range) : null;
  if (!m?.[1] || !m[2]) return undefined;
  return Number(m[1]) * UNIT_MS[m[2] as keyof typeof UNIT_MS];
}

/** The ranges the picker offers (WEB-F-DEV-04); a widget's configured range is added when it is another one. */
export const RANGES = ["24h", "7d", "30d"] as const;
